import React, { FC, useContext, useEffect, useState } from 'react';
import { BdsSelect } from 'blip-ds/dist/blip-ds-react';
import { useCommon } from '../../../../contexts/CommonContext';
import { capitalizeFirstLetter } from '../../../../utils/strings';
import { PageHeader } from '../../components/Metrics/PageHeader';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { RequestStatus } from '../../../../shared/Requests';
import RenderConsolidatedCards from './RenderConsolidatedCards';
import { BlipLoadingComponent } from '../../../../components/BlipLoading/BlipLoadingComponent';
import SessionConsumptionContext from '../../../../contexts/SessionConsumptionContext';
import { ConsolidatedHeader } from '../../components/Metrics/ConsolidatedHeader';
import '../../../../typings/blip-ds.d';
import {
  REPORT_PERIOD_SELECT,
  REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT,
  REPORT_SESSION_CONSUMPTION_WABAID_SELECT,
} from '../../../../shared/SegmentEvents';
import { getMonthTwoDigits } from '../../../../utils/date';
import { SessionConsumptionPaper } from '../styles';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';

const SessionConsumptionConsolidatedInformationsComponent: FC = () => {
  const {
    currentMonth,
    dateToSearch,
    setOldCompiledSessionConsumptionDataState,
    setCompiledSessionConsumptionDataState,
    content,
    tenant,
    loggedUser,
    trackingProps,
    billingSessionConsumptionApi,
    conversationTypeFilter,
    conversationWabaFilter,
    conversationTypeFilterOptions,
    conversationWabaFilterOptions,
    setConversationTypeFilter,
    setConversationWabaFilter,
    periodOptions,
    setDateToSearch,
    getPlanStatus,
    isWabaloading,
    WHATSAPP_BP_PRICING_CHANGES_CONSOLIDATED_START_DATE,
    showConsolidatedSessionNewPricing,
    setConsolidatedSessionNewPricing,
  } = useContext(SessionConsumptionContext);
  const { setHeaderContent } = useCommon();
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      setHeaderContent({
        redirect: '/data-consumption',
        text: content.formatString(content.header, tenant.info.name),
      });

      const showConsolidatedSessionNewPricing = dateToSearch >= WHATSAPP_BP_PRICING_CHANGES_CONSOLIDATED_START_DATE;
      setConsolidatedSessionNewPricing(showConsolidatedSessionNewPricing);

      const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const storageType = await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.session) ? 1 : 0;
      
      try {
        if (showConsolidatedSessionNewPricing) {
          const consolidatedSessionConsumption = await billingSessionConsumptionApi.getConsolidatedSessionConsumption(
            tenant.info.id,
            monthString,
            conversationWabaFilter,
            storageType,
          );
          setCompiledSessionConsumptionDataState({
            consolidatedSessionConsumption: consolidatedSessionConsumption,
            consolidatedContainsDataOnSelectedMonth: billingSessionConsumptionApi.getStatus() === RequestStatus.SUCCESS,
          });
        } else {
          const oldConsolidatedSessionConsumption = 
          await billingSessionConsumptionApi.getOldConsolidatedSessionConsumption(
            tenant.info.id,
            monthString,
            conversationTypeFilter,
            conversationWabaFilter,
          );
          setOldCompiledSessionConsumptionDataState({
            oldConsolidatedSessionConsumption: oldConsolidatedSessionConsumption,
            oldConsolidatedContainsDataOnSelectedMonth:
              billingSessionConsumptionApi.getStatus() === RequestStatus.SUCCESS,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [dateToSearch, conversationTypeFilter, conversationWabaFilter]);

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      date: dateToRetrieveData,
      planStatus: planStatus,
    });
  };

  const wabaOptionChangeEvent = (selectedOption) => {
    const conversationWabaValue = selectedOption.detail.value;
    setConversationWabaFilter(conversationWabaValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_WABAID_SELECT, tenant, loggedUser, {
      ...trackingProps,
      date: dateToSearch,
      whatsappConversationWaba: conversationWabaValue,
    });
  };

  const typeOptionChangeEvent = (selectedOption) => {
    const conversationTypeValue = selectedOption.detail.value;
    setConversationTypeFilter(conversationTypeValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT, tenant, loggedUser, {
      ...trackingProps,
      date: dateToSearch,
      whatsappConversationType: conversationTypeValue,
    });
  };

  const renderFilters = () => (
    <ConsolidatedHeader
      infoHeader={content.formatString(content.plan, capitalizeFirstLetter(tenant.info.plan))}
      dateToSearch={dateToSearch}
      currentMonth={currentMonth}
      middleInfoContent={content.middleInfo}
      endInfoContent={content.endInfo}
      learnMore={content.learnMore}
      modalContent={content.modalContent}
      filter1={
        conversationWabaFilterOptions.length > 0 && (
          <BdsSelect
            disabled={conversationWabaFilterOptions.length === 1}
            value={conversationWabaFilter}
            label={content.conversationWabaLabel}
            onBdsChange={wabaOptionChangeEvent}
          >
            {conversationWabaFilterOptions}
          </BdsSelect>
        )
      }
      filter2={
        !showConsolidatedSessionNewPricing && (
          <BdsSelect
            value={conversationTypeFilter}
            label={content.conversationTypeLabel}
            onBdsChange={typeOptionChangeEvent}
          >
            {conversationTypeFilterOptions}
          </BdsSelect>
        )
      }
    />
  );

  const renderLoading = () => <BlipLoadingComponent />;

  return (
    <>
      <PageHeader
        title={content.title}
        dateToSearch={dateToSearch}
        periodOptions={periodOptions}
        periodOptionChangeEvent={periodOptionChangeEvent}
      />
      <SessionConsumptionPaper>
        <div>
          {renderFilters()}
          {isWabaloading || isLoading ? renderLoading() : <RenderConsolidatedCards />}
        </div>
      </SessionConsumptionPaper>
    </>
  );
};

export default SessionConsumptionConsolidatedInformationsComponent;
